import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  ActivityIndicator,
  FlatList,
  TouchableOpacity,
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import color from "@constants/color";
import Upi from "@assets/svg/WithdrawGullakMoney/Upi";
import Bank from "@assets/svg/WithdrawGullakMoney/Bank";
import FastestTag from "@assets/svg/WithdrawGullakMoney/FastestTag";
import { OutlinedTextField } from "rn-material-ui-textfield";
import TickCircle from "@assets/svg/ShopPerformance/TickCircle";
import { getIfscDetails, getWalletOtp, validateUpi } from "@services/api";
import ButtonError from "@assets/svg/WithdrawGullakMoney/ButtonError";
import { STATE_STATUS } from "@reducers/actionTypes";
import { useDispatch, useSelector } from "react-redux";
import { actions as walletActions } from "@reducers/oldWalletReducer";
import t from "@constants/localization";
import { ACCOUNT_TYPE, AddFundBtnStatusTypes, UPI_SUFFIX } from "./utils";
import AddFundButton from "./components/AddFundButton";
import FindUpi from "./components/FindUpi";
import colors from "@utils/styles/colors";
import { ScaledSheet } from '@utils/scale';

const GullakAddFund = ({ route, navigation }) => {
  const PATH = "screens/WithDrawGullakMoney/addFund";
  const dispatch = useDispatch();
  const { createFundAccountStatus, createFundAccountError } = useSelector(
    (state) => state.wallet,
  );
  const [accountNo, setAccountNo] = useState("");
  const [confirmAccountNo, setConfirmAccountNo] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [ifscDetails, setIfscDetails] = useState(null);
  const [accountHolderName, setAccountHolderName] = useState("");
  const [upiId, setUpiId] = useState("");
  const [buttonStatus, setButtonStatus] = useState(
    AddFundBtnStatusTypes.DEFAULT,
  );
  const [buttonText, setButtonText] = useState(t(`${PATH}/submit`));

  const [showUpiSuffix, setShowUpiSuffix] = useState(false);

  const [accountNoError, setAccountNoError] = useState(null);
  const [confirmAccountError, setConfirmAccountError] = useState(null);
  const [ifscError, setIfscError] = useState(null);
  const [ifscLoading, setIfscLoading] = useState(0);
  const [isMatchingAccountNo, setIsMatchingAccountNo] = useState(0);
  const [accountNameError, setAccountNameError] = useState(null);
  const [upiError, setUpiError] = useState(null);
  const { type } = route.params;

  useEffect(() => {
    if (createFundAccountStatus === STATE_STATUS.CREATED) {
      dispatch(walletActions.fetchFundAccounts());
      dispatch(walletActions.resetFundAccountBank());
      navigation.navigate("WithDrawGullakMoney");
    }
    if (createFundAccountStatus === STATE_STATUS.CREATE_FAILED) {
      const errors =
        createFundAccountError &&
        createFundAccountError.createAccountResult &&
        createFundAccountError.createAccountResult.errors;
      if (errors && errors.account_number) {
        setAccountNoError(errors.account_number.message);
      }
      if (errors && errors.ifsc) {
        setIfscError(errors.ifsc.message);
      }
      if (errors && errors.name) {
        setAccountNameError(errors.name.message);
      } else {
        if (
          createFundAccountError &&
          createFundAccountError.error &&
          createFundAccountError.error === "INVALID_OTP"
        ) {
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createFundAccountStatus]);

  const bankInputFields = [
    {
      key: "accountNo",
      label: t(`${PATH}/accountNumber`),
      keyboardType: "numeric",
      onChangeText: (text) => setAccountNo(text.replace(/[^0-9]/g, "")),
      value: accountNo,
      error: accountNoError,
      secureTextEntry: true,
    },
    {
      key: "confirmAccountNo",
      label: t(`${PATH}/confirmAccountNumber`),
      keyboardType: "numeric",
      onChangeText: (text) => {
        setConfirmAccountNo(text.replace(/[^0-9]/g, ""));
        if (text === accountNo) {
          setIsMatchingAccountNo(1);
        } else if (text.length >= accountNo.length) {
          setIsMatchingAccountNo(-1);
        } else {
          setIsMatchingAccountNo(0);
        }
      },
      value: confirmAccountNo,
      error: confirmAccountError,
      secureTextEntry: false,
    },
    {
      key: "ifscCode",
      label: t(`${PATH}/ifscCode`),
      keyboardType: "default",
      onChangeText: (text) => {
        validateIFSC(text);
        setIfscCode(text);
      },
      value: ifscCode,
      error: ifscError,
      secureTextEntry: false,
      maxLength: 11,
    },
    {
      key: "accountHolderName",
      label: t(`${PATH}/accountHolderName`),
      keyboardType: "default",
      onChangeText: (text) =>
        setAccountHolderName(text.replace(/[^A-Za-z _]/gi, "")),
      value: accountHolderName,
      error: accountNameError,
      secureTextEntry: false,
    },
  ];

  const createFundAccount = async () => {
    if (type === "bank_account") {
      if (accountNo.length < 1) {
        setAccountNoError(t(`${PATH}/pleaseEnterAccountNumber`));
        return;
      }
      if (confirmAccountNo.length < 1) {
        setConfirmAccountError(t(`${PATH}/pleaseEnterConfirmAccountNumber`));
        return;
      }
      if (accountNo !== confirmAccountNo) {
        setConfirmAccountError(t(`${PATH}/accountNumberDoesNotMatch`));
        return;
      }
      if (ifscCode.length < 1) {
        setIfscError(t(`${PATH}/pleaseEnterIFSCCode`));
        return;
      }
      if (accountHolderName.length < 1) {
        setAccountNameError(t(`${PATH}/pleaseEnterAccountHolderName`));
        return;
      }
      if (ifscLoading !== 2) {
        return;
      }
      setButtonStatus(AddFundBtnStatusTypes.LOADING);
      setButtonText(t(`${PATH}/verifying`));
      sendOtpToUser();
    } else {
      if (upiId.length < 1) {
        setUpiError(t(`${PATH}/pleaseEnterUPIid`));
        return;
      }
      try {
        setButtonStatus(AddFundBtnStatusTypes.LOADING);
        setButtonText(t(`${PATH}/verifying`));
        const res = await validateUpi({ upi_address: upiId.trim() });
        if (res.data.success === true) {
          const customer_name = res.data.customer_name
            .replace(/\s\s+/g, " ")
            .toLowerCase()
            .split(" ")
            .map((el) => el[0].toUpperCase() + el.substring(1))
            .join(" ");
          setButtonStatus(AddFundBtnStatusTypes.SUCCESS);
          setButtonText(t(`${PATH}/verified`));
          sendOtpToUser(customer_name);
        }
      } catch (error) {
        setButtonStatus(AddFundBtnStatusTypes.ERROR);
        setButtonText(t(`${PATH}/invalidUPI`));
      }
    }
  };

  const sendOtpToUser = async (customer_name = "") => {
    if (type === "bank_account") {
      const bankPayload = {
        account_type: type,
        ifsc: ifscCode,
        account_number: accountNo,
        name: accountHolderName.replace(/\s\s+/g, " ").trim(),
      };
      try {
        const res = await getWalletOtp({
          type: "create",
          isDebug: __DEV__,
          ...bankPayload,
        });
        setButtonStatus(AddFundBtnStatusTypes.SUCCESS);
        setButtonText("OTP sent");
        navigation.navigate("VerifyAccount", {
          type: "create",
          item: {
            ...bankPayload,
          },
        });
      } catch (error) {
        setButtonStatus(AddFundBtnStatusTypes.ERROR);
        setButtonText(t(`${PATH}/submit`));
      }
    } else {
      const vpaPayload = {
        account_type: ACCOUNT_TYPE.vpa,
        upi_address: upiId,
        name: customer_name,
      };
      try {
        const res = await getWalletOtp({
          type: "create",
          isDebug: __DEV__,
          ...vpaPayload,
        });
        setButtonStatus(AddFundBtnStatusTypes.SUCCESS);
        setButtonText("OTP sent");
        navigation.navigate("VerifyAccount", {
          type: "create",
          item: {
            ...vpaPayload,
          },
        });
      } catch (error) {
        setButtonStatus(AddFundBtnStatusTypes.ERROR);
        setButtonText(t(`${PATH}/submit`));
      }
    }
  };

  const validateIFSC = async (ifsc) => {
    if (ifsc.length === 11) {
      try {
        setIfscLoading(1);
        const res = await getIfscDetails(ifsc);
        console.log("res", res);
        setIfscDetails(res.data);
        setIfscLoading(2);
      } catch (e) {
        setIfscDetails(false);
        setIfscLoading(-1);
      }
    } else {
      setIfscDetails(null);
      setIfscLoading(0);
    }
  };

  const renderIfscRightAccessory = () => {
    if (ifscCode.length !== 11) {
      return null;
    }
    if (ifscLoading === 1) {
      return <ActivityIndicator color={color.extraLightBlack} />;
    } else if (ifscLoading === 2) {
      return (
        <View style={{ paddingBottom: 6 }}>
          <TickCircle width={14} height={14} />
        </View>
      );
    } else if (ifscLoading === -1) {
      return <ButtonError />;
    } else {
      return null;
    }
  };

  const renderAccountRightAccessory = () => {
    if (confirmAccountNo.length < 1) {
      return null;
    }
    if (isMatchingAccountNo === 1) {
      return (
        <View style={{ paddingBottom: 6 }}>
          <TickCircle width={14} height={14} />
        </View>
      );
    } else if (isMatchingAccountNo === -1) {
      return <ButtonError />;
    } else {
      return <ActivityIndicator color={color.extraLightBlack} />;
    }
  };

  const renderUpiSuffix = ({ item }) => (
    <TouchableOpacity
      testID="wgm-upi-suffix-item"
      style={styles.upiSuffixItem}
      onPress={() => handleUpiSuffix(item)}
    >
      <Text style={styles.upiSuffixText}>{`${item}`}</Text>
    </TouchableOpacity>
  );

  const handleUpiSuffix = (item) => {
    setUpiId(`${upiId}${item}`);
    setShowUpiSuffix(false);
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.white }}>
      <HeaderWithBackAndTitle
        navigation={navigation}
        headertitle={
          type === "bank_account"
            ? t(`${PATH}/bankAccount`)
            : t(`${PATH}/upiID`)
        }
        showBackButton={true}
        route={route}
      />
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps="handled"
        bottomOffset={20}
      >
          <Text style={styles.KeyboardAvoidingViewStyle}>
            {type === "bank_account"
              ? t(`${PATH}/addBankAccount`)
              : t(`${PATH}/addUPIID`)}
          </Text>
          <View style={styles.modeContainer}>
            <View style={styles.rowContainer}>
              <View style={{ flexDirection: "row" }}>
                {type === "bank_account" ? <Bank /> : <Upi />}
                <Text style={styles.tabHeaderText}>
                  {type === "bank_account"
                    ? t(`${PATH}/bankTransfer`)
                    : t(`${PATH}/upiID`)}
                </Text>
                {type === "vpa" ? <FastestTag /> : null}
              </View>
            </View>
            {type === "bank_account" ? (
              bankInputFields.map((inputField, index) => (
                <>
                  <OutlinedTextField
                    key={inputField.key}
                    label={inputField.label}
                    keyboardType={inputField.keyboardType}
                    onChangeText={(text) => {
                      if (buttonStatus !== AddFundBtnStatusTypes.DEFAULT) {
                        setButtonStatus(AddFundBtnStatusTypes.DEFAULT);
                        setButtonText(t(`${PATH}/submit`));
                      }
                      inputField.onChangeText(text);
                    }}
                    value={inputField.value}
                    error={inputField.error}
                    errorColor={color.red}
                    autoCapitalize="characters"
                    containerStyle={{
                      width: "100%",
                      marginTop: 10,
                    }}
                    inputContainerStyle={{
                      height: 50,
                    }}
                    tintColor={color.extraLightBlack}
                    activeLineWidth={1}
                    labelFontSize={12}
                    labelTextStyle={{
                      marginLeft: "34%",
                    }}
                    maxLength={inputField.maxLength}
                    secureTextEntry={inputField.secureTextEntry}
                    contentInset={{ top: 0, input: 10, label: 2 }}
                    renderRightAccessory={() => {
                      if (inputField.key === "ifscCode") {
                        return renderIfscRightAccessory();
                      } else if (inputField.key === "confirmAccountNo") {
                        return renderAccountRightAccessory();
                      }
                      return null;
                    }}
                  />
                  {inputField.key === "ifscCode" &&
                    ifscLoading === 2 &&
                    ifscDetails && (
                      <View
                        style={{
                          flexDirection: "row",
                          width: "95%",
                          marginTop: -10,
                        }}
                      >
                        <Text
                          style={{ fontSize: 12, color: color.plain_green }}
                        >
                          {ifscDetails.BANK}, {ifscDetails.BRANCH}
                        </Text>
                      </View>
                    )}
                </>
              ))
            ) : (
              <>
                <OutlinedTextField
                  label="UPI ID"
                  keyboardType="default"
                  onChangeText={(text) => {
                    if (buttonStatus !== AddFundBtnStatusTypes.DEFAULT) {
                      setButtonStatus(AddFundBtnStatusTypes.DEFAULT);
                      setButtonText(t(`${PATH}/submit`));
                    }
                    if (text[text.length - 1] === "@") {
                      setShowUpiSuffix(true);
                    } else {
                      setShowUpiSuffix(false);
                    }
                    setUpiId(text);
                  }}
                  value={upiId}
                  error={upiError}
                  errorColor={color.red}
                  containerStyle={{
                    width: "100%",
                    marginTop: 10,
                  }}
                  inputContainerStyle={{
                    height: 50,
                  }}
                  tintColor={color.extraLightBlack}
                  activeLineWidth={1}
                  labelFontSize={12}
                  labelTextStyle={{
                    marginLeft: "34%",
                  }}
                  secureTextEntry={false}
                  contentInset={{ top: 0, input: 10, label: 2 }}
                />
                {showUpiSuffix && (
                  <FlatList
                    data={UPI_SUFFIX}
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    keyExtractor={(item) => item}
                    renderItem={renderUpiSuffix}
                  />
                )}
              </>
            )}
            <View style={{ alignItems: "center", minHeight: 15 }} />
            <AddFundButton
              text={buttonText}
              type={buttonStatus}
              disabled={buttonStatus !== AddFundBtnStatusTypes.DEFAULT}
              onPress={() => createFundAccount()}
            />
            {type !== "bank_account" ? (
              <>
                <View style={styles.divider} />
                <FindUpi navigation={navigation} />
              </>
            ) : null}
          </View>
      </KeyboardAwareScrollView>
    </View>
  );
};

const styles = ScaledSheet.create({
  modeContainer: {
    backgroundColor: "#F6F8FA",
    width: "90%",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 20,
    padding: 16,
    borderRadius: 16,
  },
  rowContainer: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 20,
  },
  KeyboardAvoidingViewStyle: {
    marginHorizontal: 20,
    marginTop: 20,
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 6,
    color: color.mediumBlacklightBlack,
  },
  tabHeaderText: {
    paddingHorizontal: 10,
    fontSize: 16,
    fontWeight: "700",
    color: color.black,
  },
  upiSuffixItem: {
    backgroundColor: "rgba(242, 242, 242, 1)",
    padding: 4,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginHorizontal: 5,
    borderColor: color.blank_grey,
    borderWidth: 0.5,
    alignContent: "center",
    justifyContent: "center",
  },
  upiSuffixText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
  },
  divider: {
    width: "110%",
    height: 1,
    backgroundColor: color.blank_grey,
    marginVertical: 20,
  },
});

export default GullakAddFund;
